import React, { useReducer, useMemo, useEffect, useRef } from 'react'
import { AnalyticsContext, analyticsReducer, useAnalyticsContext, useAnalyticsDashboard } from 'AnalyticsDashboard/AnalyticsContext'
import Filters from 'AnalyticsDashboard/components/Filters'
import type { AnalyticsState } from 'AnalyticsDashboard/context/analyticsReducer'

function getHashParam(key: string): string | null {
  const hash = window.location.hash
  const queryIndex = hash.indexOf('?')
  if (queryIndex === -1) return null
  const params = new URLSearchParams(hash.slice(queryIndex))
  return params.get(key)
}

function setHashParam(key: string, value: string | null) {
  const hash = window.location.hash
  const queryIndex = hash.indexOf('?')
  const basePath = queryIndex === -1 ? hash : hash.slice(0, queryIndex)
  const params = new URLSearchParams(queryIndex === -1 ? '' : hash.slice(queryIndex))
  if (value !== null && value !== undefined) {
    params.set(key, value)
  } else {
    params.delete(key)
  }
  const qs = params.toString()
  window.location.hash = qs ? `${basePath}?${qs}` : basePath
}

function readFiltersFromUrl(): Record<string, any> | null {
  try {
    const encoded = getHashParam('f')
    if (!encoded) return null
    const json = decodeURIComponent(escape(atob(encoded)))
    const parsed = JSON.parse(json)
    return (parsed && typeof parsed === 'object') ? parsed : null
  } catch {
    return null
  }
}

function writeFiltersToUrl(filterValues: Record<string, any>) {
  const hasValues = Object.values(filterValues).some(v => v !== undefined && v !== null)
  if (hasValues) {
    const json = JSON.stringify(filterValues)
    const encoded = btoa(unescape(encodeURIComponent(json)))
    setHashParam('f', encoded)
  } else {
    setHashParam('f', null)
  }
}

interface AnalyticsProviderProps {
  children: React.ReactNode
  uiEnabled?: boolean
  filterValues?: Record<string, any>
  global?: boolean
  requiredFilters?: string[]
  onMakeDefault?: ((filterValues: Record<string, any>) => void) | null
}

export default function AnalyticsProvider({
  children,
  uiEnabled = true,
  filterValues = {},
  global = false,
  requiredFilters = [],
  onMakeDefault = null
}: AnalyticsProviderProps) {
  const urlFilters = readFiltersFromUrl()
  const [state, dispatch] = useReducer(analyticsReducer, {
    queriesInfo: [],
    queriesInfoLoaded: false,
    queries: {},
    filterValues: urlFilters || filterValues,
    uiEnabled,
    global,
    requiredFilters,
    local: {
      loading: true
    }
  } as AnalyticsState)

  const value = useMemo(() => ({ state, dispatch }), [state])

  return (
    <AnalyticsContext.Provider value={value}>
      <div className="analytics-dashboard">
        <FilterUrlSync />
        { uiEnabled && <Filters onMakeDefault={onMakeDefault} defaultFilterValues={filterValues} /> }
        <AnalyticsContainer>
          {children}
        </AnalyticsContainer>
      </div>
    </AnalyticsContext.Provider>
  )
}

function FilterUrlSync() {
  const { state: { filterValues } } = useAnalyticsContext()
  const isInitialMount = useRef(true)

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    writeFiltersToUrl(filterValues)
  }, [filterValues])

  return null
}

function AnalyticsContainer({ children }: { children: React.ReactNode }) {
  const { fetchQueriesInfo } = useAnalyticsDashboard()
  const queriesInfoLoaded = useAnalyticsContext().state.queriesInfoLoaded

  useEffect(() => {
    fetchQueriesInfo()
  }, [])

  if (!queriesInfoLoaded) return null

  return <>
    { children }
  </>
}
